import { Close, Save } from "@mui/icons-material";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
  Box,
} from "@mui/material";
import axios from "axios";
import { useEffect, useState, useMemo } from "react";
import { hostname, parseError } from "../../utils";
import LoadingButton from "../../components/LoadingButton";
import { CONTENT_STRATEGY_FIELD_LIMITS } from "../../constants";

interface ContentStrategy {
  id?: string;
  name: string;
  salesNarrative: string;
  targetAudience: string;
  pillars: any[];
  created_date?: string;
  updated_date?: string;
}

const DEFAULT_STRATEGY: ContentStrategy = {
  name: "",
  salesNarrative: "",
  targetAudience: "",
  pillars: [],
};

export default ({
  open,
  onClose,
  companyId,
  getToken,
  eventListener,
  strategy,
}: {
  open: boolean;
  onClose: (refresh?: boolean) => void;
  companyId: string;
  getToken: Function;
  eventListener: Function;
  strategy: ContentStrategy | null;
}) => {
  const [formData, setFormData] = useState<ContentStrategy>(DEFAULT_STRATEGY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (strategy) {
        setFormData(strategy);
      } else {
        setFormData(DEFAULT_STRATEGY);
      }
    }
  }, [open, strategy]);

  const handleInputChange = (field: string, value: string) => {
    setFormData({
      ...formData,
      [field]: value,
    });
  };

  // Validation errors
  const validationErrors = useMemo(() => {
    const errors: Record<string, string> = {};

    if (!formData.name?.trim()) {
      errors.name = "Strategy name is required";
    } else if (formData.name.length > CONTENT_STRATEGY_FIELD_LIMITS.name) {
      errors.name = `Name must be ${CONTENT_STRATEGY_FIELD_LIMITS.name} characters or less`;
    }

    if (formData.salesNarrative && formData.salesNarrative.length > CONTENT_STRATEGY_FIELD_LIMITS.salesNarrative) {
      errors.salesNarrative = `Sales Narrative must be ${CONTENT_STRATEGY_FIELD_LIMITS.salesNarrative} characters or less`;
    }

    if (formData.targetAudience && formData.targetAudience.length > CONTENT_STRATEGY_FIELD_LIMITS.targetAudience) {
      errors.targetAudience = `Target Audience must be ${CONTENT_STRATEGY_FIELD_LIMITS.targetAudience} characters or less`;
    }

    return errors;
  }, [formData.name, formData.salesNarrative, formData.targetAudience]);

  const isFormValid = Object.keys(validationErrors).length === 0 && formData.name?.trim();

  const handleSave = async () => {
    if (!formData.name) {
      eventListener({
        type: "SET_ERROR",
        error: "Strategy name is required",
      });
      return;
    }

    setSaving(true);
    try {
      const token = await getToken();
      const response = await axios.post(
        `${hostname}/content/strategy`,
        {
          companyId,
          strategy: {
            ...formData,
            companyId,
          },
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      eventListener({
        type: "SET_INFO",
        info: strategy ? "Strategy updated successfully" : "Strategy created successfully",
      });
      onClose(true);
    } catch (error) {
      console.error("Error saving content strategy:", error);
      eventListener({
        type: "SET_ERROR",
        error: parseError(error),
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    onClose(false);
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6">
            {strategy ? "Edit Content Strategy" : "New Content Strategy"}
          </Typography>
          <IconButton onClick={handleCancel}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <TextField
          label="Strategy Name"
          fullWidth
          value={formData.name}
          onChange={(e) => handleInputChange("name", e.target.value)}
          required
          margin="normal"
          error={!!validationErrors.name}
          helperText={
            validationErrors.name ||
            `${formData.name?.length || 0}/${CONTENT_STRATEGY_FIELD_LIMITS.name} characters`
          }
          inputProps={{ maxLength: CONTENT_STRATEGY_FIELD_LIMITS.name }}
        />

        <TextField
          label="Sales Narrative"
          fullWidth
          multiline
          rows={4}
          value={formData.salesNarrative}
          onChange={(e) => handleInputChange("salesNarrative", e.target.value)}
          margin="normal"
          placeholder="The sales narrative defines your overall messaging approach and value proposition"
          error={!!validationErrors.salesNarrative}
          helperText={
            validationErrors.salesNarrative ||
            `${formData.salesNarrative?.length || 0}/${CONTENT_STRATEGY_FIELD_LIMITS.salesNarrative} characters`
          }
          inputProps={{ maxLength: CONTENT_STRATEGY_FIELD_LIMITS.salesNarrative }}
        />

        <TextField
          label="Target Audience"
          fullWidth
          multiline
          rows={2}
          value={formData.targetAudience}
          onChange={(e) => handleInputChange("targetAudience", e.target.value)}
          margin="normal"
          placeholder="Describe your target audience and their key characteristics"
          error={!!validationErrors.targetAudience}
          helperText={
            validationErrors.targetAudience ||
            `${formData.targetAudience?.length || 0}/${CONTENT_STRATEGY_FIELD_LIMITS.targetAudience} characters`
          }
          inputProps={{ maxLength: CONTENT_STRATEGY_FIELD_LIMITS.targetAudience }}
        />

        <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
          After creating your strategy, you'll be able to define content pillars and organize them by funnel stage.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel}>Cancel</Button>
        <LoadingButton
          loading={saving}
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={!isFormValid}
          startIcon={<Save />}
        >
          {strategy ? "Update" : "Create"}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};